import React, { useEffect, useMemo, useState } from "react";
import { History, Plus, Search, Wrench, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../context/AuthContext";
import { BulkCheckbox, BulkHeaderCheck } from "./BulkSelectContext";
import HistoryDialog from "./HistoryDialog";
import RepairsDialog from "./RepairsDialog";
import ItemDialog from "./ItemDialog";
import EquipmentBatchDialog from "./EquipmentBatchDialog";
import PickupTargetDialog from "./PickupTargetDialog";
import { ITEM_TYPES } from "./constants";
import { fmtRoDate } from "../../lib/dateFmt";

// Deduce auto categoria fină din nume — primul cuvânt sau pattern match
const KEYWORD_MAP = [
  { kw: /airless|wagner.*hyper|graco/i, cat: "Airless" },
  { kw: /messschr|mess-schr|messgerät/i, cat: "Messschreiber" },
  { kw: /șlefuit|slefuit|schleif|sander|festool ets|festool rs/i, cat: "Mașină de șlefuit" },
  { kw: /aspirator|sauger|staubsauger/i, cat: "Aspirator" },
  { kw: /pistol.*vopsit|pistol.*spray|farbspritz/i, cat: "Pistol vopsit" },
  { kw: /compresor|kompressor/i, cat: "Compresor" },
  { kw: /generator/i, cat: "Generator" },
  { kw: /ferăstrău|ferastrau|säge|saw/i, cat: "Ferăstrău" },
  { kw: /bormașin|bormasin|bohr/i, cat: "Bormașină" },
  { kw: /polizor|winkelschle|grinder/i, cat: "Polizor unghiular" },
  { kw: /înșurub|insurub|akkuschrauber|schrauber/i, cat: "Înșurubelniță electrică" },
  { kw: /lampă|lampa|leuchte|flutlicht/i, cat: "Lampă lucru" },
  { kw: /scară|scara|leiter/i, cat: "Scară" },
  { kw: /schele|gerüst|gerust/i, cat: "Schele" },
];

const autoDeriveCategory = (it) => {
  if (it.equipment_category && String(it.equipment_category).trim()) {
    return String(it.equipment_category).trim();
  }
  const name = (it.name || "").trim();
  if (!name) return "(fără categorie)";
  for (const { kw, cat } of KEYWORD_MAP) {
    if (kw.test(name)) return cat;
  }
  // Fallback: primul cuvânt din nume (capitalize)
  const first = name.split(/[\s\-/]+/)[0];
  return first ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase() : "(fără categorie)";
};

export default function ProductsView({ meta, items, stores, cars, persons = [], historyCounts = {}, onChanged }) {
  const { canDo, user } = useAuth();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [colorFilter, setColorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialog, setDialog] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [repairsFor, setRepairsFor] = useState(null);
  const [pickupFor, setPickupFor] = useState(null);
  const [collapsedCats, setCollapsedCats] = useState(() => new Set());

  // Preîncarcă sugestiile de categorie pentru ItemDialog (datalist)
  useEffect(() => {
    api.get("/inventory/equipment-categories")
      .then((r) => {
        if (Array.isArray(r.data?.all)) {
          window.__equipmentCategorySuggestions = r.data.all;
        }
      })
      .catch(() => { /* ignore */ });
  }, [items.length]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((i) => !q || `${i.name || ""} ${i.note || ""} ${i.equipment_category || ""}`.toLowerCase().includes(q))
      .filter((i) => typeFilter === "all" || i.item_type === typeFilter)
      .filter((i) => colorFilter === "all" || i.color === colorFilter)
      .filter((i) => statusFilter === "all" || i.status === statusFilter);
  }, [items, search, typeFilter, colorFilter, statusFilter]);

  // Grupare: equipment_category (auto-dedus) → items, ordine alfabetică
  const groups = useMemo(() => {
    const map = new Map();
    for (const it of filtered) {
      const cat = autoDeriveCategory(it);
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(it);
    }
    // Sortare items in fiecare grup: stoc primul, apoi alfabetic
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const aAssigned = a?.assignee_person_id ? 1 : 0;
        const bAssigned = b?.assignee_person_id ? 1 : 0;
        if (aAssigned !== bAssigned) return aAssigned - bAssigned;
        return (a.name || "").localeCompare(b.name || "", "ro");
      });
    }
    // Sortare categorii: alfabetic, dar „(fără categorie)" la urmă
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === "(fără categorie)") return 1;
      if (b === "(fără categorie)") return -1;
      return a.localeCompare(b, "ro");
    });
  }, [filtered]);

  const toggleCat = (cat) => {
    setCollapsedCats((s) => {
      const n = new Set(s);
      if (n.has(cat)) n.delete(cat); else n.add(cat);
      return n;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={16} className="text-zinc-400"/>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Caută..." className="border-0 shadow-none focus-visible:ring-0" data-testid="inv-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate tipurile</SelectItem>
            {ITEM_TYPES.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={colorFilter} onValueChange={setColorFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate culorile</SelectItem>
            {meta.colors.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Orice status</SelectItem>
            <SelectItem value="functional">Funcțional</SelectItem>
            <SelectItem value="defect">Defect</SelectItem>
          </SelectContent>
        </Select>
        {canDo("INVENTAR", "add") && (
          <>
            <Button
              onClick={() => setDialog({ mode: "batch-equipment" })}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="products-add-batch-equipment-btn"
              title="Adaugă echipamente cu numerotare automată prefixată (ex: G1, G2 pentru Graco)"
            >
              <Plus size={16}/> Lot cu serie auto
            </Button>
            <Button onClick={() => setDialog({ mode: "add" })} className="bg-[var(--primary)]" data-testid="products-add-item-btn"><Plus size={16}/> Produs nou</Button>
          </>
        )}
      </div>

      <div className="text-[11px] text-zinc-500 px-1" data-testid="products-groups-summary">
        {groups.length} {groups.length === 1 ? "categorie" : "categorii"} · {filtered.length} produse total
      </div>

      {/* Grupuri */}
      <div className="space-y-3">
        {groups.map(([catName, list]) => {
          const isCollapsed = collapsedCats.has(catName);
          const catSlug = catName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          return (
            <div key={catName} className="card-flat overflow-hidden" data-testid={`products-group-${catSlug}`}>
              <div className="w-full flex items-center bg-zinc-50 hover:bg-zinc-100 border-b border-[var(--border)]">
                <button
                  type="button"
                  onClick={() => toggleCat(catName)}
                  className="flex-1 flex items-center gap-2 px-4 py-2.5 transition-colors text-left"
                  data-testid={`products-group-toggle-${catSlug}`}
                >
                  {isCollapsed ? <ChevronRight size={16} className="text-zinc-500"/> : <ChevronDown size={16} className="text-zinc-500"/>}
                  <h3 className="font-display font-bold text-sm uppercase tracking-wider text-zinc-800">{catName}</h3>
                  <span className="text-[11px] text-zinc-500 font-semibold">({list.length})</span>
                </button>
                {canDo("INVENTAR", "add") && (
                  <button
                    type="button"
                    onClick={() => setDialog({ mode: "batch-equipment", defaultCategory: catName.toLowerCase() })}
                    className="text-[10px] uppercase tracking-wider font-bold px-3 py-1 mr-3 rounded border border-emerald-400 text-emerald-700 bg-white hover:bg-emerald-50"
                    data-testid={`products-group-batch-${catSlug}`}
                    title={`Adaugă lot „${catName}" cu numerotare automată`}
                  >
                    + Lot serie auto
                  </button>
                )}
              </div>

              {!isCollapsed && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                        <th className="p-3 w-8"><BulkHeaderCheck items={list}/></th>
                        <th className="p-3 w-8"></th>
                        <th className="p-3">Denumire</th>
                        <th className="p-3">Culoare / Echipă</th>
                        <th className="p-3">Atribuit la persoană / locație</th>
                        <th className="p-3">Cumpărat</th>
                        <th className="p-3">Garanție</th>
                        <th className="p-3 text-right">Cant.</th>
                        <th className="p-3 text-center">Reparații</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((it) => {
                        const c = meta.colors.find((x) => x.code === it.color);
                        const defect = it.status === "defect";
                        const repairCount = Array.isArray(it.repairs) ? it.repairs.length : 0;
                        const today = new Date().toISOString().slice(0, 10);
                        const warrantyExpired = it.warranty_until && it.warranty_until < today;
                        const warrantySoon = it.warranty_until && !warrantyExpired && (() => {
                          const exp = new Date(it.warranty_until);
                          const diff = (exp - new Date()) / (1000 * 60 * 60 * 24);
                          return diff <= 30;
                        })();
                        return (
                          <tr key={it.id} className={`border-t border-[var(--border)] row-hover ${!it.serial && !it.no_serial ? "bg-amber-50/60" : ""}`}>
                            <td className="p-3 w-8"><BulkCheckbox id={it.id}/></td>
                            <td className="p-3"><span className="inline-block w-3 h-3 rounded-full" style={{ background: defect ? "#EF4444" : "#10B981" }} title={defect ? "Defect" : "Funcțional"}/></td>
                            <td className="p-3 font-medium">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => setHistoryFor(it)}
                                  className="hover:text-cyan-700 hover:underline cursor-pointer flex items-center gap-1 group"
                                  title="Vezi istoricul complet (cumpărare, mutări, predări)"
                                  data-testid={`item-name-history-${it.id}`}
                                >
                                  <span>{it.name}</span>
                                  <History size={10} className="opacity-0 group-hover:opacity-60 transition-opacity"/>
                                </button>
                                {(historyCounts[it.id] || 0) > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => setHistoryFor(it)}
                                    className="text-[9px] font-bold tabular-nums bg-cyan-100 text-cyan-800 hover:bg-cyan-200 px-1.5 py-0.5 rounded-full"
                                    title={`${historyCounts[it.id]} evenimente — click pentru detalii`}
                                    data-testid={`item-history-count-${it.id}`}
                                  >📊 {historyCounts[it.id]}</button>
                                )}
                                <span className="text-[10px] text-zinc-400">· {ITEM_TYPES.find((t) => t.code === it.item_type)?.label || it.item_type}</span>
                              </div>
                              {it.supplier && <div className="text-[10px] text-blue-700 mt-0.5">🏭 {it.supplier}</div>}
                              {it.serial && <div className="text-[10px] text-zinc-500 font-mono mt-0.5">SN: {it.serial}</div>}
                              {!it.serial && it.no_serial && <div className="text-[10px] text-zinc-400 italic mt-0.5">SN: fără serie</div>}
                              {!it.serial && !it.no_serial && (
                                <div className="mt-1 -mx-1 px-2 py-1 rounded bg-amber-400 text-amber-900 font-extrabold text-[11px] flex items-center gap-1.5 shadow-sm" data-testid={`item-missing-serial-${it.id}`} title="Nr. de serie lipsește. Editează item-ul pentru a completa.">
                                  <span className="text-sm">⚠️</span> LIPSĂ NR. SERIE
                                </div>
                              )}
                            </td>
                            <td className="p-3 text-xs">
                              {c ? (
                                <span className="inline-flex items-center gap-1.5 font-semibold px-2 py-0.5 rounded-full" style={{ background: `${c.hex}22`, color: c.hex, border: `1px solid ${c.hex}40` }}>
                                  <span className="w-3 h-3 rounded-full" style={{ background: c.hex }}/>
                                  {c.label}
                                </span>
                              ) : <span className="text-zinc-400">—</span>}
                              {it.location && <div className="text-[10px] text-zinc-500 mt-0.5">📍 {it.location}{it.shelf_row && it.shelf_col ? ` · ${it.shelf_row}${it.shelf_col}` : ""}</div>}
                            </td>
                            <td className="p-3 text-xs text-zinc-600">
                              {it.assignee_person_id || it.assignee ? (
                                <div className="space-y-0.5" data-testid={`item-allocation-${it.id}`}>
                                  <div className="flex items-center gap-1 text-emerald-800 font-bold text-[12px]" title={`Echipament atribuit la ${it.assignee}`}>
                                    <span>👤</span>
                                    <span className="truncate max-w-[160px]">{it.assignee || "—"}</span>
                                  </div>
                                  {it.assigned_at && (
                                    <div className="text-[10px] text-zinc-600">
                                      📅 <strong>din {fmtRoDate(it.assigned_at.slice(0, 10))}</strong>
                                    </div>
                                  )}
                                  {it.assigned_by && <div className="text-[10px] text-zinc-500">predat de <strong>{it.assigned_by}</strong></div>}
                                  {it.recipient_car_label && <div className="text-[10px] text-emerald-700">🚗 {it.recipient_car_label}</div>}
                                </div>
                              ) : it.assigned_at ? (
                                <div className="space-y-0.5" data-testid={`item-allocation-${it.id}`}>
                                  <div className="text-[10px] text-amber-700">📍 la locație din</div>
                                  <div className="font-semibold text-zinc-700">{fmtRoDate(it.assigned_at.slice(0, 10))}</div>
                                  {it.assigned_by && <div className="text-[10px] text-zinc-500">de <strong>{it.assigned_by}</strong></div>}
                                </div>
                              ) : <span className="text-zinc-400">—</span>}
                            </td>
                            <td className="p-3 text-xs text-zinc-600">
                              {it.purchase_date ? <span className="font-semibold">{fmtRoDate(it.purchase_date)}</span> : <span className="text-zinc-400">—</span>}
                            </td>
                            <td className="p-3 text-xs">
                              {it.warranty_until ? (
                                <span className={`font-semibold ${warrantyExpired ? "text-red-700" : warrantySoon ? "text-amber-700" : "text-emerald-700"}`} title={warrantyExpired ? "Garanție expirată" : warrantySoon ? "Garanție expiră în <30 zile" : "Garanție activă"}>
                                  {fmtRoDate(it.warranty_until)} {warrantyExpired ? "⛔" : warrantySoon ? "⚠" : "✓"}
                                </span>
                              ) : <span className="text-zinc-400">—</span>}
                            </td>
                            <td className="p-3 text-right tabular-nums">{it.quantity || 1}{it.unit ? ` ${it.unit}` : ""}</td>
                            <td className="p-3 text-center">
                              <button
                                type="button"
                                onClick={() => setRepairsFor(it)}
                                className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded transition-colors ${repairCount > 0 ? "bg-orange-100 text-orange-800 hover:bg-orange-200" : "text-zinc-500 hover:bg-zinc-100"}`}
                                title={repairCount > 0 ? `${repairCount} reparații înregistrate` : "Înregistrează reparație"}
                                data-testid={`item-repairs-btn-${it.id}`}
                              >
                                <Wrench size={12}/>
                                {repairCount > 0 ? repairCount : <span className="text-[10px]">log</span>}
                              </button>
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex gap-1 justify-end">
                                {it.assignee_person_id && canDo("INVENTAR", "edit") && (
                                  <button onClick={() => setPickupFor(it)} className="text-xs text-cyan-700 hover:underline font-semibold" title="Preia / Transferă la alt angajat sau locație" data-testid={`item-pickup-${it.id}`}>Preia</button>
                                )}
                                <button onClick={() => setDialog({ mode: "move", item: it })} className="text-xs text-[var(--primary)] hover:underline" data-testid={`item-move-${it.id}`}>Mută</button>
                                <button onClick={() => setDialog({ mode: "edit", item: it })} className="text-xs text-zinc-600 hover:underline" data-testid={`item-edit-${it.id}`}>Editează</button>
                                <button onClick={() => setHistoryFor(it)} className="text-zinc-500 p-1 hover:bg-zinc-100 rounded" title="Istoric mutări/echipe" data-testid={`item-history-${it.id}`}><History size={12}/></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {groups.length === 0 && (
          <div className="card-flat p-12 text-center text-zinc-500 italic" data-testid="products-empty">
            Niciun produs care să corespundă filtrelor.
          </div>
        )}
      </div>

      {dialog && dialog.mode === "batch-equipment" && (
        <EquipmentBatchDialog
          defaultCategory={dialog.defaultCategory || ""}
          meta={meta}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); onChanged(); }}
        />
      )}
      {dialog && dialog.mode !== "batch-equipment" && (
        <ItemDialog mode={dialog.mode} item={dialog.item} meta={meta} stores={stores} cars={cars}
          onClose={() => setDialog(null)} onSaved={() => { setDialog(null); onChanged(); }} />
      )}
      {historyFor && <HistoryDialog item={historyFor} onClose={() => setHistoryFor(null)} />}
      {repairsFor && <RepairsDialog item={repairsFor} onClose={() => setRepairsFor(null)} onChanged={onChanged} />}
      {pickupFor && (
        <PickupTargetDialog
          item={pickupFor}
          locations={(meta?.locations || []).map((l) => ({ key: l, label: l }))}
          persons={persons}
          defaultLocationLabel={user?.pickup_location || "SERGHEI ACASA"}
          onClose={() => setPickupFor(null)}
          onDone={() => { setPickupFor(null); onChanged(); }}
        />
      )}
    </div>
  );
}
